import moment from "moment-timezone";

// One place for "what time is it now" and for comparing times, so no screen
// keeps its own copy (like the shared ranking for bids).
//
// The auction server's clock is the official one: it decides whether a bid is
// in time. Screens follow it too, so a computer clock that is wrong (or was
// changed) can't make a countdown or the Bid / Prebid button disagree with
// the server.
//
// How far this computer's clock is from the server is measured:
// - once when the app opens,
// - again when the tab comes back into view or the internet comes back,
// - silently from the serverTime the server sends with live updates.
// Never on a timer, so it adds no load.

let offsetMs = 0;

const toMs = (value) => {
  if (value === null || value === undefined || value === "") return NaN;
  return typeof value === "number" ? value : new Date(value).getTime();
};

// server time in ms, as a plain number (what react-countdown's `now` wants)
export const nowMs = () => Date.now() + offsetMs;

// server time as a moment, in the user's time zone
export const now = () => moment(nowMs()).tz(getUserTimeZone());

// the zone picked on the Dashboard ("Select Zones"), else the browser's own
export const getUserTimeZone = () => {
  try {
    return localStorage.getItem("currentTimeZone") || moment.tz.guess();
  } catch (error) {
    return moment.tz.guess();
  }
};

// The server's time as it was `latencyMs` ago (half the round trip of the
// request that brought it). Ignored when missing or not a real time.
export const applyServerTime = (serverTime, latencyMs = 0) => {
  const serverMs = toMs(serverTime);
  if (!Number.isFinite(serverMs)) return;
  offsetMs = serverMs + latencyMs - Date.now();
};

// asks the server for its time once; keeps the last good offset on failure
export const syncServerTime = async () => {
  const sentAt = Date.now();
  try {
    // loaded only when syncing, so screens that just show dates don't pull in
    // the HTTP client
    const { default: timeSyncApi } = await import("../api/time-sync.api");
    const { data } = await timeSyncApi();
    const latencyMs = (Date.now() - sentAt) / 2;
    applyServerTime(data.serverTime ?? data.utcDateTime, latencyMs);
  } catch (error) {
    // no answer: keep using the last offset (or the computer clock)
  }
};

let watching = false;

// Called once by the app: syncs now, and again when the tab comes back into
// view or the internet comes back (the computer may have slept or changed
// its clock meanwhile).
export const startServerTimeSync = () => {
  syncServerTime();
  if (watching) return;
  watching = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") syncServerTime();
  });
  window.addEventListener("online", syncServerTime);
};

// true when `value` is still ahead of the server's now (missing = false)
export const isAfterNow = (value) => {
  const ms = toMs(value);
  return Number.isFinite(ms) && ms > nowMs();
};

// true when `value` has passed by the server's now (missing = false)
export const isBeforeNow = (value) => {
  const ms = toMs(value);
  return Number.isFinite(ms) && ms < nowMs();
};

// The one way a date and time is shown, everywhere: 13/10/2026 04:12 PM.
// Countdowns ("Ends in 1h 59m") are not dates and keep their own look.
export const DATE_TIME_DISPLAY = "DD/MM/YYYY hh:mm A";
export const DATE_DISPLAY = "DD/MM/YYYY";
// Bid times show seconds: the earlier of two equal bids wins, so a bidder or
// seller must be able to see which came first (13/10/2026 04:12:07 PM)
export const BID_TIME_DISPLAY = "DD/MM/YYYY hh:mm:ss A";
// the header clock ticks, so it also shows seconds
export const CLOCK_DISPLAY = BID_TIME_DISPLAY;

// a date and time as shown on screen, in the user's time zone ("–" if none)
export const displayDateTime = (value, zone = getUserTimeZone()) => {
  const ms = toMs(value);
  return Number.isFinite(ms)
    ? moment(ms).tz(zone).format(DATE_TIME_DISPLAY)
    : "–";
};

// what a table column sorts on: the real time, never the shown text
export const dateSortValue = (value) => {
  const ms = toMs(value);
  return Number.isFinite(ms) ? ms : 0;
};

// test helper: forget the measured offset
export const resetServerTimeForTests = () => {
  offsetMs = 0;
};
